import { Box, Paper, Stack, Typography, useTheme } from "@mui/material";
import { type FC, type ReactElement, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SystemDialog from "../../../components/UIToolkit/Popups/Dialog";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { LETTER_SPACING } from "../../../theme/tokens";
import type { PbjModelUpsertBody, PbjPrediction } from "../../../utils/models";
import { prefersReducedMotion } from "../../../utils/reducedMotion";
import { usePbjPredictionJobCoverage } from "../hooks/usePbjPredictionManualHours";
import { useSavePbjTrackedPlan } from "../hooks/usePbjTrackedPlan";
import { ActionItems } from "./ActionItems";
import { AuditHoursSourceBanner } from "./AuditHoursSourceBanner";
import { CategoryBreakdownTable } from "./CategoryBreakdownTable";
import { cardShadow } from "./colors";
import { ManualHoursButton } from "./ManualHoursButton";
import { MissingJobsWarning } from "./MissingJobsWarning";
import { PbjPredictionJobCoverageStatus } from "./PbjPredictionJobCoverageStatus";
import { PlanSummaryCard } from "./PlanSummaryCard";
import { PredictionHero } from "./PredictionHero";
import type { PbjDraftControls } from "./pbjDraftControls";
import { computePlanOutcome } from "./planOutcome.utils";
import { PREDICTION_CONTAINER, PREDICTION_MULTICOL_QUERY } from "./predictionLayout.consts";
import { type PeriodMode, RecommendationsSidebar } from "./RecommendationsSidebar";
import { type PlanItem, RECOMMENDATION_TYPES, type RecommendationId } from "./recommendations.constants";
import { resolveTargetPoints } from "./resolveTargetPoints.utils";
import { buildTrackedPlanSaveBody, trackerButtonState } from "./trackedPlan.utils";

// Map the BE-computed greedy `suggestedPlan` into the FE PlanItem[] shown in the Action
// Plan. Each step's `actionId` is a 1:1 RecommendationId; the per-category breakdown is
// enriched with each category's title (from the already-fetched `prediction.categories`)
// so ActionItems can render the per-bucket HPPD/points rows. BE-supplied values already
// account for cross-category coupling and are trusted verbatim.
const seedPlanFromSuggested = (prediction: PbjPrediction): PlanItem[] =>
	prediction.suggestedPlan.steps.flatMap((step): PlanItem[] => {
		const rec = RECOMMENDATION_TYPES.find((r) => r.id === step.actionId);
		if (!rec) return [];
		const breakdown = step.breakdown.map((b) => ({
			categoryId: b.categoryId,
			categoryTitle: prediction.categories.find((c) => c.id === b.categoryId)?.title ?? b.categoryId,
			hppdGap: b.hppdGap,
			points: b.pointsGained,
		}));
		return [
			{
				id: rec.id,
				titleKey: rec.titleKey,
				spend: step.spend,
				impact: step.hppdGap,
				points: step.pointsGained,
				hours: step.hours,
				hoursCatchup: step.hoursCatchup,
				actionKey: rec.actionKey,
				actionCatchupKey: rec.actionCatchupKey,
				breakdown,
			},
		];
	});

type Props = {
	prediction: PbjPrediction;
	// Non-null: PbjModelPage always supplies configDraft (never null) — see PbjDraftControls.
	draft: PbjModelUpsertBody;
	onDraftChange: (next: PbjModelUpsertBody) => void;
	// Persist + recompute a specific draft — used by the manual-turnover / CMI / target dialogs.
	onCalculate: (next: PbjModelUpsertBody) => void;
	// Committed ISO date range — forwarded to the sidebar so its "Suggest plan" re-optimization can
	// call the suggested-plan endpoint for the right window, to the hero for its period context
	// pill, and to the Manual Hours dialog + missing-job warning so both key off the same window
	// the score was computed against.
	startDate: string;
	endDate: string;
	// The persisted row's CMI pair — the hero compares the draft against it to detect an
	// admin edit since load (drives the CASPER effective-value display).
	savedCmi: { pccPdpmAvg: number; natNursingCaseMixIndex: number };
	// Wraps the hero's "Change period" affordance in the page-level period/facility popover.
	renderChangePeriod: (trigger: ReactElement) => ReactNode;
	// Committing a plan is the one action on this tab whose result lives on another one; landing
	// the user there saves them hunting for what their click did.
	onPlanTracked: () => void;
};

/**
 * The Prediction tab body — the "one look" composition: a compact hero orientation band
 * (score lead + gap story + actions) above a 2×2 grid of four independent cards. Top row =
 * the analysis: WHY (the scoring breakdown with inline meters) | HOW (the recommendation
 * levers). Bottom row = the plan: Required Actions (under Why) | Plan Summary (under How).
 * Owns the plan state (`selectedPlan`) shared by the sidebar + ActionItems + PlanSummaryCard,
 * the lifted `planOutcome` the hero + summary read from, and the tracked-plan commit the
 * sidebar's "Add to tracker" reports into. Below the container threshold the grid collapses to
 * a single column (breakdown → recommendations → required → summary). When the plan is empty
 * the bottom two cards render null, so only the top row shows — no empty cells.
 */
export const ScoringAndRecommendations: FC<Props> = ({
	prediction,
	draft,
	savedCmi,
	onDraftChange,
	onCalculate,
	startDate,
	endDate,
	renderChangePeriod,
	onPlanTracked,
}) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const theme = useTheme();
	// Group the draft + persist/recompute callbacks once, then drill them through the hero,
	// the breakdown table, and the recommendations sidebar as a single prop.
	const draftControls: PbjDraftControls = { draft, onDraftChange, onCalculate };
	// The suggested plan is shown in the Action Plan BY DEFAULT — seeded on first render
	// so the user sees it without clicking "Suggest plan" or "Add all".
	const [selectedPlan, setSelectedPlan] = useState<PlanItem[]>(() => seedPlanFromSuggested(prediction));
	const actionItemsRef = useRef<HTMLDivElement>(null);

	// Re-seed the Action Plan from the freshly-computed suggestion whenever a new
	// prediction arrives (Calculate / quarter / Set-Target Save all produce a new
	// `prediction` object ref + a re-run greedy).
	useEffect(() => {
		setSelectedPlan(seedPlanFromSuggested(prediction));
	}, [prediction]);
	// Default to "qtd" only when we actually have an in-progress window (elapsed
	// days < quarter days); otherwise the quarter is complete and the two are equal.
	const [periodMode, setPeriodMode] = useState<PeriodMode>(prediction.days > 0 && prediction.days < prediction.quarterDays ? "qtd" : "quarter");

	const handleAddToPlan = useCallback((item: PlanItem) => {
		setSelectedPlan((prev) => {
			if (prev.some((p) => p.id === item.id)) return prev;
			requestAnimationFrame(() => actionItemsRef.current?.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "start" }));
			return [...prev, item];
		});
	}, []);

	const handleUpdatePlan = useCallback((item: PlanItem) => {
		setSelectedPlan((prev) => prev.map((p) => (p.id === item.id ? item : p)));
	}, []);

	const handleRemove = useCallback((id: RecommendationId) => {
		setSelectedPlan((prev) => prev.filter((p) => p.id !== id));
	}, []);

	// Reset the Action Plan to the freshly-suggested default — same state a page refresh
	// would produce. The sidebar separately re-seeds its sliders so the whole section resets.
	const handleResetPlan = useCallback(() => {
		setSelectedPlan(seedPlanFromSuggested(prediction));
	}, [prediction]);

	// Use the BE-resolved ADC (user override → computed → fallback) so the recommendation
	// HPPD-to-hours math matches what the scorer actually used.
	const census = prediction.resolvedAverageCensus;
	// Resolve the buffered target through the single source of truth so the plan outcome
	// (reachesTarget) and the hero's gauge/meter/verdict measure against the SAME number.
	const { targetPoints } = resolveTargetPoints(prediction, draft);
	const canSuggest = prediction.suggestedPlan.steps.length > 0;

	// Lift the Action-Plan outcome (projected score, cost, reaches-target, headline hours) to
	// this owner so the HERO can render the verdict + the gauge's projected arc from the SAME
	// numbers the "How" column's plan uses. Recomputes whenever the plan or the prediction
	// changes — so dragging a lever / Suggest-plan / Set Target all update the hero live.
	const planOutcome = useMemo(
		() =>
			computePlanOutcome({
				selectedPlan,
				categories: prediction.categories,
				currentPoints: prediction.totalPoints,
				targetPoints,
				census,
				quarterDays: prediction.quarterDays,
				quarterWeekendDays: prediction.quarterWeekendDays,
				daysRemaining: prediction.daysRemaining,
				periodMode,
			}),
		[
			selectedPlan,
			prediction.categories,
			prediction.totalPoints,
			targetPoints,
			census,
			prediction.quarterDays,
			prediction.quarterWeekendDays,
			prediction.daysRemaining,
			periodMode,
		],
	);

	// --- Add to tracker -----------------------------------------------------
	// The payload is assembled HERE because this component owns both `selectedPlan` and the
	// resolved target; the sidebar only renders the button and reports the click.
	const trackedPlanBody = useMemo(
		() =>
			buildTrackedPlanSaveBody({
				departmentId: prediction.config.departmentId,
				selectedPlan,
				categories: prediction.categories,
				periodMode,
				committedStart: startDate,
				targetPoints,
			}),
		[prediction.config.departmentId, prediction.categories, selectedPlan, periodMode, startDate, targetPoints],
	);
	const trackerState = useMemo(() => trackerButtonState(prediction.trackedPlan, trackedPlanBody), [prediction.trackedPlan, trackedPlanBody]);
	const savePlan = useSavePbjTrackedPlan();
	const [switchConfirmOpen, setSwitchConfirmOpen] = useState(false);

	// --- Missing-job warning -------------------------------------------------
	// Jobs the prediction has no hours for. Dismissal is deliberately PER SESSION and held in
	// component state, not persisted: the warning is about this quarter's data, and a user who
	// dismisses it today should still be told next time they open the tab if the hours are still
	// missing. Keyed by the committed window so changing the quarter re-asks rather than inheriting
	// a dismissal that was about a different period.
	// Not asked at all on the audit basis: the endpoint answers "which InHouse jobs have no SCHEDULED
	// hours", which stops being the same question as "which jobs are missing from your score" the
	// moment the score comes from payroll punches instead (DAT-597). See the hook for the two ways it
	// would mislead.
	const auditHoursSource = prediction.hoursSource === "audit";
	const auditUnmappedHours = prediction.auditUnmappedHours;
	if (auditHoursSource && typeof auditUnmappedHours !== "number") {
		throw new Error("Audit-backed PBJ predictions require auditUnmappedHours");
	}
	const coverageQuery = usePbjPredictionJobCoverage(prediction.config.departmentId, startDate, endDate, !auditHoursSource);
	const [dismissedWindow, setDismissedWindow] = useState<string | null>(null);
	const coverageWindowKey = `${startDate}..${endDate}`;
	const missingJobs =
		!auditHoursSource && coverageQuery.data !== undefined && !coverageQuery.isLoading && !coverageQuery.isError && dismissedWindow !== coverageWindowKey
			? coverageQuery.data
			: undefined;
	const dismissMissingJobsWarning = useCallback(() => {
		if (coverageQuery.data === undefined || coverageQuery.isError) return;
		track("pbj_model_missing_jobs_warning_dismissed", {
			department_id: prediction.config.departmentId,
			jobs_count: coverageQuery.data.length,
		});
		setDismissedWindow(coverageWindowKey);
	}, [track, prediction.config.departmentId, coverageQuery.data, coverageQuery.isError, coverageWindowKey]);

	const commitTrackedPlan = useCallback(
		(isSwitch: boolean) => {
			track(isSwitch ? "pbj_model_tracker_plan_switched" : "pbj_model_tracker_plan_added", {
				department_id: prediction.config.departmentId,
				actions_count: trackedPlanBody.actions.length,
				period_mode: trackedPlanBody.periodMode,
				target_points: trackedPlanBody.targetPoints,
			});
			savePlan.mutate(trackedPlanBody, { onSuccess: () => onPlanTracked() });
		},
		[track, prediction.config.departmentId, trackedPlanBody, savePlan, onPlanTracked],
	);

	// A settled "Tracked" confirmation is not re-pressable, so only the other two states act.
	const handleTrackPlan = useCallback(() => {
		if (trackerState === "tracked") return;
		if (trackerState === "switch") {
			setSwitchConfirmOpen(true);
			return;
		}
		commitTrackedPlan(false);
	}, [trackerState, commitTrackedPlan]);

	const handleConfirmSwitch = useCallback(() => {
		setSwitchConfirmOpen(false);
		commitTrackedPlan(true);
	}, [commitTrackedPlan]);

	return (
		// Establish a container so the hero + grid can respond to the CONTENT-PANE width
		// (which the left rail narrows) rather than the viewport.
		<Stack spacing={{ xs: 2.5, md: 3 }} sx={{ containerType: "inline-size", containerName: PREDICTION_CONTAINER }}>
			{/* Above the hero, so it frames every number below it rather than annotating one card. */}
			{auditHoursSource && typeof auditUnmappedHours === "number" && <AuditHoursSourceBanner unmappedHours={auditUnmappedHours} />}
			<PredictionHero
				prediction={prediction}
				draftControls={draftControls}
				savedCmi={savedCmi}
				planOutcome={planOutcome}
				periodStart={startDate}
				periodEnd={endDate}
				renderChangePeriod={renderChangePeriod}
			/>

			{/* One look → a 2×2 grid of four independent cards. Top row (analysis): WHY breakdown |
			    HOW recommendations. Bottom row (the plan): Required Actions (under Why) | Plan Summary
			    (under How). Grid rows auto-size, so each ROW aligns its own two cells — a big win over
			    the old "force one giant Why column to match the whole How stack" approach: the breakdown
			    now shares its row only with the recommendations, and the bottom row sits under them.
			    `alignItems: start` keeps every card at its natural height. Now that the Why breakdown
				    carries an always-on description per measure it stands close to the taller How column on
				    its own, so the top row is left natural too (no stretch); any small remaining gap is honest
				    breathing room, not padding. Below the container threshold the grid
			    collapses to one column in DOM order: breakdown → recommendations → required → summary.
			    When the plan is empty, ActionItems + PlanSummaryCard both return null, so the bottom row
			    never materializes and the top row stands alone (no empty cells). */}
			<Box
				sx={{
					display: "grid",
					gap: 2.5,
					gridTemplateColumns: "1fr",
					alignItems: "start",
					[PREDICTION_MULTICOL_QUERY]: { gridTemplateColumns: "1fr 1fr" },
				}}
				data-testid="pbj-model-one-look"
			>
				{/* WHY — top-left. Eyebrow rides inside the card (as before). Natural height — the
				    always-on measure descriptions give the breakdown enough height to sit close to the
				    recommendations without a forced stretch. */}
				<Paper
					elevation={0}
					sx={{
						minWidth: 0,
						borderRadius: 3,
						bgcolor: "background.paper",
						boxShadow: cardShadow(theme),
						overflow: "hidden",
					}}
					data-testid="pbj-model-why-panel"
				>
					{/* Eyebrow left, warning + "Manual Hours" right. The warning is intentionally compact:
					    hover exposes the explanation and the full missing-job list without pushing the
					    analysis grid down. Manual hours remain beside the HPPD rows they move. */}
					<Stack
						direction="row"
						sx={{ alignItems: "center", justifyContent: "space-between", gap: 1.5, px: { xs: 2.5, md: 3 }, pt: { xs: 2.5, md: 3 }, pb: 1 }}
					>
						<Typography
							variant="caption"
							sx={{ fontWeight: 700, letterSpacing: LETTER_SPACING.LABEL, textTransform: "uppercase", color: theme.palette.primary.main }}
						>
							{t("pbj_model.prediction.why.eyebrow")}
						</Typography>
						<Stack direction="row" sx={{ alignItems: "center", gap: 0.5 }}>
							{missingJobs && (
								<MissingJobsWarning gaps={missingJobs} variant="summary" onDismiss={dismissMissingJobsWarning} dataTestId="pbj-model-missing-jobs-warning" />
							)}
							<ManualHoursButton
								departmentId={prediction.config.departmentId}
								startDate={startDate}
								endDate={endDate}
								prediction={prediction}
								/* Attention-carrying only while something is actually missing — a permanently
								   loud button stops meaning anything. */
								emphasized={missingJobs !== undefined && missingJobs.length > 0}
							/>
						</Stack>
					</Stack>
					{!auditHoursSource && (
						<PbjPredictionJobCoverageStatus isLoading={coverageQuery.isLoading} isError={coverageQuery.isError} dataTestId="pbj-model-missing-jobs-coverage" />
					)}
					<Box sx={{ px: { xs: 2.5, md: 3 }, pb: { xs: 2.5, md: 3 } }}>
						<CategoryBreakdownTable prediction={prediction} draftControls={draftControls} />
					</Box>
				</Paper>

				{/* HOW — top-right. Eyebrow rides above the recommendations card. */}
				<Box sx={{ minWidth: 0 }} data-testid="pbj-model-how-panel">
					<Typography
						variant="caption"
						sx={{
							display: "block",
							fontWeight: 700,
							letterSpacing: LETTER_SPACING.LABEL,
							textTransform: "uppercase",
							color: theme.palette.primary.main,
							mb: 1,
						}}
					>
						{t("pbj_model.prediction.how.eyebrow")}
					</Typography>
					<RecommendationsSidebar
						prediction={prediction}
						census={census}
						draftControls={draftControls}
						selectedPlan={selectedPlan}
						onAddToPlan={handleAddToPlan}
						onUpdatePlan={handleUpdatePlan}
						onResetPlan={handleResetPlan}
						canSuggest={canSuggest}
						periodMode={periodMode}
						onPeriodModeChange={setPeriodMode}
						startDate={startDate}
						endDate={endDate}
						trackerState={trackerState}
						onTrackPlan={handleTrackPlan}
						isTrackingPlan={savePlan.isPending}
					/>
				</Box>

				{/* Required Actions — bottom-left, under Why. A standalone card; the scroll ref rides
				    its Paper so it can be a direct grid cell that disappears when the plan is empty. */}
				<ActionItems
					scrollRef={actionItemsRef}
					selectedPlan={selectedPlan}
					categories={prediction.categories}
					onRemove={handleRemove}
					census={census}
					quarterDays={prediction.quarterDays}
					quarterWeekendDays={prediction.quarterWeekendDays}
					daysRemaining={prediction.daysRemaining}
					periodMode={periodMode}
				/>

				{/* Plan Summary — bottom-right, under How. The bold dark "final result" bookend card;
				    reads the lifted planOutcome + target + star bands (never recomputes); null for an
				    empty plan. */}
				<PlanSummaryCard
					planOutcome={planOutcome}
					targetPoints={targetPoints}
					starThresholds={prediction.starThresholds}
					census={census}
					quarterDays={prediction.quarterDays}
					daysRemaining={prediction.daysRemaining}
					periodMode={periodMode}
				/>
			</Box>
			{/* Switching is a fresh save, not an edit: the old plan stays in the department's history
			    and the tracker re-measures from today. Worth a confirm because the progress already
			    accrued against the previous commitment stops being what the tab reports. */}
			<SystemDialog
				open={switchConfirmOpen}
				onClose={() => setSwitchConfirmOpen(false)}
				title={t("pbj_model.tracker.switch_confirm.title")}
				body={t("pbj_model.tracker.switch_confirm.body")}
				confirm={t("pbj_model.tracker.switch_confirm.confirm")}
				confirmButtonVariant="primary"
				onConfirm={handleConfirmSwitch}
				confirmLoading={savePlan.isPending}
				dataTestId="pbj-model-tracker-switch-confirm"
			/>
		</Stack>
	);
};
